import { Tag } from 'baseui/tag';
import { SliderHorizontalSVG, XCircleSVG } from 'common/assets';
import { tagOverridesStyles } from 'common/constants/styles';
import { Filter } from 'common/repositories/common.model';
import stylesColors from 'common/styles/styles.colors';
import ButtonWithRef from 'components/elements/buttons/button-with-ref';
import { FilterHandleProvider } from 'hooks/filter/use-filter-handle-component';
import useFilterHandleParent from 'hooks/filter/use-filter-handle-parent';
import useFilterHandleTransformer from 'hooks/filter/use-filter-handle-transformer';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import styles from 'styles/Filter.module.css';

import DefaultQueryFilter from './default-query-filter';
import FilterControl from './filter-control';
import {
  afterBeforeFilterGroup,
  afterBeforeLabelFormatter,
  tagLabelFormatter,
} from './utils';

export interface QueryFilterProps {
  filters: Filter[];
  isCompact?: boolean;
  isUseResponseOptions?: boolean;
}

export default function QueryFilter(props: QueryFilterProps) {
  const { isCompact = true, isUseResponseOptions = false } = props;
  const { t } = useTranslation();
  const [filters, setFilters] = React.useState<Filter[]>([]);
  const parentFilterHandler = useFilterHandleParent();
  const childrenFilterHandler = useFilterHandleTransformer(filters, setFilters);
  const tagTransfomerAction = React.useCallback(
    (filter: Filter) => {
      if (filter.name.includes('after')) {
        const filterGroup = afterBeforeFilterGroup(
          filter.name.replace('_after', ''),
          filters,
        );
        return {
          label: afterBeforeLabelFormatter(filterGroup),
          onActionClick: () => {
            parentFilterHandler.onClearFilter(filter.name)();
            parentFilterHandler.onClearFilter(
              filter.name.replace('_after', '_before'),
            )();
          },
        };
      }

      return {
        label: tagLabelFormatter(filter),
        onActionClick: parentFilterHandler.onClearFilter(filter.name),
      };
    },
    [filters, parentFilterHandler],
  );

  React.useEffect(() => {
    if (typeof props.filters === 'undefined') return;
    setFilters(props.filters);
  }, [props.filters]);

  if (filters.length === 0) {
    return null;
  }

  if (isCompact === false) {
    return (
      <FilterHandleProvider
        filters={filters}
        setFilters={setFilters}
        isUseResponseOptions={isUseResponseOptions}
      >
        <DefaultQueryFilter />
      </FilterHandleProvider>
    );
  }

  return (
    <div className={styles.filterContainer}>
      <FilterHandleProvider
        filters={filters}
        setFilters={setFilters}
        isUseResponseOptions={isUseResponseOptions}
      >
        <div className={styles.filterBaseContainer}>
          {filters.map((filter) => {
            const currentValue = filter.value || filter?.default;

            if (!currentValue) return null;
            if (filter.name.includes('before')) return null;

            const { label, onActionClick } = tagTransfomerAction(filter);

            return (
              <FilterControl name={filter.name} key={filter.name}>
                {(toggleFilter) => (
                  <div className={styles.filterItemContainer}>
                    <Tag
                      onClick={toggleFilter}
                      onActionClick={onActionClick}
                      overrides={tagOverridesStyles}
                    >
                      {label}
                    </Tag>
                  </div>
                )}
              </FilterControl>
            );
          })}
          <FilterControl>
            {() => (
              <ButtonWithRef
                startEnhancer={() => (
                  <SliderHorizontalSVG
                    color={stylesColors.primaryDefault}
                    size={16}
                  />
                )}
              >
                {t('common:filter')}
              </ButtonWithRef>
            )}
          </FilterControl>
          {childrenFilterHandler.filtersHasValues && (
            <ButtonWithRef
              onClick={() => {
                parentFilterHandler.onClearFilters(filters);
              }}
              startEnhancer={() => {
                return (
                  <XCircleSVG color={stylesColors.primaryDefault} size={16} />
                );
              }}
            >
              {t('common:clear_filter')}
            </ButtonWithRef>
          )}
        </div>
      </FilterHandleProvider>
    </div>
  );
}
